//--------------------------------------------------------------------//
//******************** deploymentConsoleTab *****************************//
//--------------------------------------------------------------------//
// Open Terminal button (same window.open pattern as agent-ssh.tab.js's
// openTerminal(), pointed at the deployment-manager's terminal UI route)
// plus a logs viewer that tails the deployed app's stdout/stderr.
// Unlike Agents, there is no SSH key management here — the deployment
// manager auto-generates and auto-registers its own keypair server-side.
import deploymentConsoleTemplateUrl from './deployment-console.template.html';
import { GUDHUB_SETTINGS } from '../../../config.js';
import { getAuthData } from '../deployments.request.js';

angular.module('deploymentsTableModule')

.directive('deploymentConsoleTab', ['DeploymentsService', function (DeploymentsService) {
    return {
        restrict: 'E',
        scope: {
            deployment: '='
        },
        controller: ['$scope', 'localizationService', function ($scope, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            $scope.loadingLogs = false;
            $scope.logsError = '';
            $scope.log = null;
            $scope.noContainer = false;
            $scope.pulling = false;
            $scope.pullError = '';

            // Explains why there's no log output, based on the deployment's own
            // state (no need to parse the backend's 409 error text): a stopped
            // container still exists (Stop keeps it), a killed one doesn't
            // (Kill clears container_id but deployed_at proves it ran before),
            // and a deployment that's never had a container yet has neither.
            $scope.getEmptyLogsMessage = function () {
                if ($scope.deployment.container_id) {
                    return $scope.translate('Container is stopped.');
                }
                if ($scope.deployment.deployed_at) {
                    return $scope.translate('Container was killed.');
                }
                return $scope.translate("This deployment hasn't been deployed yet.");
            };

            // A plain browser navigation can't carry an Authorization header,
            // so the token rides along as a query param — the backend reads
            // it from there for this route and re-embeds it in the returned
            // page so the WebSocket it opens can authenticate too.
            // The window is opened synchronously (before the await) so it
            // stays tied to this click's user gesture — popup blockers can
            // reject window.open() called after an async gap.
            $scope.openTerminal = async function () {
                if (!$scope.deployment._id || $scope.deployment.status !== 'running') return;
                const win = window.open('', 'TerminalWin', 'width=900,height=600,left=100,top=200,resizable=yes,scrollbars=yes');
                const authData = await getAuthData();
                const url = GUDHUB_SETTINGS.deployment_manager_server_url + '/deployment-terminal-ui/' + $scope.deployment._id
                    + '?token=' + encodeURIComponent(authData.token);
                if (win) win.location.href = url;
            };

            // Runs `git pull` inside the existing container (deploy-pipeline.js's
            // pullDeployment()) — no new container/ports, rebuilds/restarts only
            // if there were new commits. Refreshes the logs panel afterward since
            // a restart (or a failed pull) both show up there.
            $scope.pullLatest = async function () {
                if ($scope.pulling || !$scope.deployment._id || $scope.deployment.status !== 'running') return;

                $scope.pulling = true;
                $scope.pullError = '';
                $scope.$applyAsync();

                try {
                    const updated = await DeploymentsService.pull($scope.deployment._id);
                    Object.assign($scope.deployment, updated);
                    if (updated.status === 'error') {
                        $scope.pullError = updated.last_error || 'Pull failed';
                    }
                } catch (error) {
                    console.log('Failed to pull latest:', error);
                    $scope.pullError = error.message || 'Failed to pull latest';
                } finally {
                    $scope.pulling = false;
                    $scope.$applyAsync();
                    $scope.refreshLogs();
                }
            };

            $scope.refreshLogs = async function () {
                if (!$scope.deployment._id) return;

                $scope.loadingLogs = true;
                $scope.logsError = '';
                $scope.$applyAsync();

                try {
                    const result = await DeploymentsService.getLogs($scope.deployment._id);
                    $scope.log = result.log;
                    $scope.noContainer = result.noContainer;
                } catch (error) {
                    console.log('Failed to fetch deployment logs:', error);
                    $scope.logsError = error.message || 'Failed to fetch logs';
                } finally {
                    $scope.loadingLogs = false;
                    $scope.$applyAsync();
                }
            };

            // The Info tab's Deploy/Stop/Kill buttons mutate this same
            // deployment object by reference (Object.assign, not a new
            // object) — mutating properties doesn't by itself re-trigger
            // this tab's own logic, so watch for the transitions those
            // actions cause and refresh whenever one happens, regardless of
            // which tab/button triggered it. Watching both fields (not just
            // status) catches Kill-right-after-Stop, where status stays
            // 'stopped' but container_id changes from set to null.
            $scope.$watchGroup(['deployment.status', 'deployment.container_id'], function (newValues, oldValues) {
                if (newValues !== oldValues) {
                    $scope.refreshLogs();
                }
            });

            // Eagerly load logs when the tab's directive is instantiated (the
            // popup keeps both tabs' directives alive via ng-show, same as
            // agent-skills-tab/agent-groups-tab load on instantiation regardless
            // of which tab is currently visible).
            $scope.refreshLogs();
        }],
        templateUrl: deploymentConsoleTemplateUrl
    };
}]);
